import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  InboxArrowDownIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { ConfirmModal } from '../../components/common';
import PasosVerificacion, { pasoDeVerificacion } from '../../components/verificacion/PasosVerificacion';
import { useAuth } from '../../contexts/AuthContext';
import { loteAfiliacionSepService, type FichaSep, type LoteSepDetalle } from '../../services/api';
import { mensajeDeError } from '../../utils/consultas';
import { formatFechaCalendario } from '../../utils/fechas';
import { numeroCorto } from '../../utils/lotesAfiliacion';
import {
  ESTADO_LOTE_SEP,
  ETIQUETA_CONTROL,
  ETIQUETA_SUPUESTO_LOTE,
  ROLES_APERTURA_LOTE,
  ROLES_RECEPCION_LOTE,
  desplazaAOtroPartido,
} from '../../utils/lotesAfiliacionSep';

type Filtro = 'todas' | 'observaciones' | 'obligatorias' | 'no_entregadas';

function partidosDondeFigura(detalleInterno: string | null): string {
  const tramo = detalleInterno?.split(' · ').find(t => t.startsWith('Afiliado en: '));
  return tramo ? tramo.replace('Afiliado en: ', '') : 'otro partido';
}

function pasoDelLote(lote: LoteSepDetalle): { paso: number; detalle?: string; alerta?: boolean } {
  if (lote.estado === 'PRESENTADO') return { paso: 1, detalle: 'Esperando el papel' };
  if (lote.estado === 'FICHAS_RECIBIDAS') return { paso: 2, detalle: 'Por abrir' };
  if (lote.estado === 'RESUELTO') return { paso: 8 };
  if (!lote.verificacion) return { paso: 2 };
  return {
    paso: pasoDeVerificacion(lote.verificacion.estado),
    alerta: lote.verificacion.estado === 'EN_SUBSANACION',
  };
}

function descargarPlanilla(lote: LoteSepDetalle) {
  const filas = lote.solicitudes
    .filter(s => s.fichaRecibida)
    .sort((a, b) => (a.numeroFicha ?? 0) - (b.numeroFicha ?? 0))
    .map(s => ({ 'N° ficha': s.numeroFicha, DNI: s.dni, Apellido: s.apellido, Nombre: s.nombre }));
  const hoja = XLSX.utils.json_to_sheet(filas);
  hoja['!cols'] = [{ wch: 9 }, { wch: 12 }, { wch: 24 }, { wch: 24 }];
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, 'Numeración');
  XLSX.writeFile(libro, `lote-${numeroCorto(lote.numero)}-numeracion.xlsx`);
}

export default function LoteAfiliacionSepPage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: lote, isLoading, isError } = useQuery({
    queryKey: ['lote-afiliacion-sep', id],
    queryFn: () => loteAfiliacionSepService.detalle(id),
    enabled: !!id,
  });

  const actualizar = (nuevo: LoteSepDetalle) => {
    queryClient.setQueryData(['lote-afiliacion-sep', id], nuevo);
    queryClient.invalidateQueries({ queryKey: ['lotes-afiliacion-sep'] });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isError || !lote) {
    return (
      <div className="space-y-4">
        <VolverALaBandeja />
        <div className="alert-danger text-sm">No se pudo cargar el lote. Puede que no exista o que todavía no esté presentado.</div>
      </div>
    );
  }

  const estado = ESTADO_LOTE_SEP[lote.estado];
  const { paso, detalle, alerta } = pasoDelLote(lote);
  const rol = user?.rol ?? '';

  return (
    <div className="space-y-6">
      <VolverALaBandeja />

      <div className="card px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="inline-block rounded-full bg-orange-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-orange-800">
                Solo uso interno
              </span>
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Verificación Res. 011/2026{lote.expediente && ` · Expediente N° ${lote.expediente.numero}`}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Lote N° {numeroCorto(lote.numero)} · {lote.partido.denominacion}
            </h1>
            <p className="text-sm text-gray-500">
              Presentado el {formatFechaCalendario(lote.fechaPresentacion)}
              {lote.fechaRecepcionFichas
                ? ` · Papel recibido el ${formatFechaCalendario(lote.fechaRecepcionFichas)}`
                : ' · Papel todavía no recibido'}
            </p>
          </div>
          <span className={estado.clase}>{estado.etiqueta}</span>
        </div>
        <div className="mt-5">
          <PasosVerificacion paso={paso} detalleActual={detalle} alerta={alerta} />
        </div>
      </div>

      {lote.estado === 'PRESENTADO' && (
        <PanelRecepcion lote={lote} puede={ROLES_RECEPCION_LOTE.includes(rol)} onListo={actualizar} />
      )}

      {lote.estado !== 'PRESENTADO' && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <PanelSiguientePaso lote={lote} puedeAbrir={ROLES_APERTURA_LOTE.includes(rol)} onListo={actualizar} />
            <TablaFichas fichas={lote.solicitudes} />
          </div>
          <div className="space-y-6">
            <Resumen lote={lote} />
            <EfectosSobreOtrosPartidos fichas={lote.solicitudes} />
          </div>
        </div>
      )}
    </div>
  );
}

function VolverALaBandeja() {
  return (
    <Link to="/lotes-afiliacion-sep" className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700">
      <ArrowLeftIcon className="h-4 w-4" /> Volver a los lotes
    </Link>
  );
}

function AvisoSoloLectura({ texto }: { texto: string }) {
  return (
    <p className="flex items-center gap-2 text-sm text-gray-500">
      <LockClosedIcon className="h-4 w-4 flex-shrink-0" /> {texto}
    </p>
  );
}

function PanelRecepcion({ lote, puede, onListo }: { lote: LoteSepDetalle; puede: boolean; onListo: (l: LoteSepDetalle) => void }) {
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [busqueda, setBusqueda] = useState('');
  const [confirmar, setConfirmar] = useState(false);

  useEffect(() => {
    setMarcadas(new Set(lote.solicitudes.map(s => s.id)));
  }, [lote.id]);

  const recibir = useMutation({
    mutationFn: () => loteAfiliacionSepService.registrarRecepcion(lote.id, [...marcadas]),
    onSuccess: (nuevo) => {
      setConfirmar(false);
      toast.success('Recepción registrada. Las fichas quedaron numeradas.');
      onListo(nuevo);
    },
    onError: (e) => {
      setConfirmar(false);
      toast.error(mensajeDeError(e, 'No se pudo registrar la recepción.'));
    },
  });

  const total = lote.solicitudes.length;
  const faltan = total - marcadas.size;
  const texto = busqueda.trim().toLowerCase();
  const visibles = lote.solicitudes.filter(s =>
    !texto || s.dni.includes(texto) || `${s.apellido} ${s.nombre}`.toLowerCase().includes(texto));

  const alternar = (fichaId: string) => setMarcadas(prev => {
    const nuevo = new Set(prev);
    if (nuevo.has(fichaId)) nuevo.delete(fichaId); else nuevo.add(fichaId);
    return nuevo;
  });
  
  const todas = marcadas.size === total;
  const casillaTodas = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (casillaTodas.current) casillaTodas.current.indeterminate = marcadas.size > 0 && !todas;
  }, [marcadas.size, todas]);

  return (
    <div className="card">
      <div className="card-header flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-50">
            <InboxArrowDownIcon className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Qué hay que hacer ahora: recibir las fichas en papel</h2>
            <p className="text-sm text-gray-500">
              Compará el papel con esta lista y destildá las fichas que no llegaron.
            </p>
          </div>
        </div>
      </div>

      <div className="card-body space-y-4">
        {!puede && <AvisoSoloLectura texto="Solo Mesa de Entradas y el personal de la SEP pueden registrar la recepción." />}

        <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="font-semibold text-gray-900">{marcadas.size} de {total} recibidas</span>
                {faltan > 0 && <span className="badge-warning">{faltan} sin entregar</span>}
            </div>
            <BuscadorFichas valor={busqueda} onCambio={setBusqueda} />
            </div>

        <div className="max-h-[28rem] overflow-y-auto rounded-lg border border-gray-200">
        <label className={`sticky top-0 z-10 flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-medium text-gray-700 ${puede ? 'cursor-pointer' : ''}`}>
            <input
              ref={casillaTodas}
              type="checkbox"
              checked={todas}
              disabled={!puede}
              onChange={() => setMarcadas(todas ? new Set() : new Set(lote.solicitudes.map(s => s.id)))}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Llegaron todas
          </label>
          <ul className="divide-y divide-gray-100">
            {visibles.map(s => {
              const llego = marcadas.has(s.id);
              return (
                <li key={s.id}>
                  <label className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm ${llego ? '' : 'bg-amber-50'} ${puede ? 'hover:bg-gray-50' : 'cursor-default'}`}>
                    <input
                      type="checkbox"
                      checked={llego}
                      disabled={!puede}
                      onChange={() => alternar(s.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="w-24 flex-shrink-0 font-mono text-gray-600">{s.dni}</span>
                    <span className="min-w-0 flex-1 truncate text-gray-900">{s.apellido}, {s.nombre}</span>
                    {!llego && <span className="text-xs font-medium text-amber-700">No llegó</span>}
                  </label>
                </li>
              );
            })}
            {visibles.length === 0 && <li className="px-4 py-6 text-center text-sm text-gray-500">Ninguna ficha coincide con la búsqueda.</li>}
          </ul>
        </div>

        {puede && (
          <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-500">
              {marcadas.size > 0
                ? `Al confirmar, las fichas recibidas se numeran del 1 al ${marcadas.size} por apellido. Las que no llegaron no se verifican.`
                : 'Marcá al menos una ficha recibida para poder registrar la recepción.'}
            </p>
            <button onClick={() => setConfirmar(true)} disabled={marcadas.size === 0} className="btn-primary gap-2">
              <CheckCircleIcon className="h-5 w-5" /> Registrar recepción
            </button>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmar}
        onClose={() => setConfirmar(false)}
        onConfirm={() => recibir.mutate()}
        loading={recibir.isPending}
        variant={faltan > 0 ? 'warning' : 'info'}
        title="¿Registrar la recepción de las fichas?"
        message={faltan > 0
          ? `Se reciben ${marcadas.size} de ${total} fichas. Las ${faltan} que no llegaron quedan fuera de la verificación. Esto no se puede deshacer.`
          : `Se reciben las ${total} fichas del lote y se numeran del 1 al ${total}. Esto no se puede deshacer.`}
        confirmText="Sí, registrar"
      />
    </div>
  );
}

function PanelSiguientePaso({ lote, puedeAbrir, onListo }: { lote: LoteSepDetalle; puedeAbrir: boolean; onListo: (l: LoteSepDetalle) => void }) {
  const [confirmar, setConfirmar] = useState(false);

  const abrir = useMutation({
    mutationFn: () => loteAfiliacionSepService.abrirVerificacion(lote.id),
    onSuccess: (nuevo) => {
      setConfirmar(false);
      toast.success('Verificación abierta.');
      onListo(nuevo);
    },
    onError: (e) => {
      setConfirmar(false);
      toast.error(mensajeDeError(e, 'No se pudo abrir la verificación.'));
    },
  });

  const recibidas = lote.solicitudes.filter(s => s.fichaRecibida).length;

  if (lote.estado === 'FICHAS_RECIBIDAS') {
    const supuesto = lote.supuestoPrevisto ? ETIQUETA_SUPUESTO_LOTE[lote.supuestoPrevisto] ?? lote.supuestoPrevisto : null;
    return (
      <div className="card border-l-4 border-l-primary-500">
        <div className="card-body space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-50">
              <ShieldCheckIcon className="h-5 w-5 text-primary-600" />
            </div>
            <div className="space-y-1">
              <h2 className="font-semibold text-gray-900">Qué hay que hacer ahora: abrir la verificación</h2>
              <p className="text-sm text-gray-600">
                Se van a verificar las <strong>{recibidas}</strong> fichas recibidas.
                {supuesto && <> Se abre como <strong>{supuesto}</strong>.</>}
              </p>
              {lote.supuestoPrevisto === 'FICHAS_AFILIACION_ART_20' && (
                <p className="text-sm text-gray-500">
                  El partido tiene un reconocimiento o reempadronamiento en trámite, por eso estas fichas se suman a ese cómputo.
                </p>
              )}
              <p className="text-sm text-gray-500">
                Al abrirla, el control de las fichas queda congelado y el partido ya no puede cambiar nada del lote.
              </p>
            </div>
          </div>
          {!puedeAbrir && <AvisoSoloLectura texto="Solo el personal de la SEP puede abrir la verificación." />}
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button onClick={() => descargarPlanilla(lote)} className="btn-secondary gap-2">
              <DocumentArrowDownIcon className="h-5 w-5" /> Descargar planilla de numeración
            </button>
            {puedeAbrir && (
              <button onClick={() => setConfirmar(true)} className="btn-primary gap-2">
                <ShieldCheckIcon className="h-5 w-5" /> Abrir verificación
              </button>
            )}
          </div>
        </div>

        <ConfirmModal
          isOpen={confirmar}
          onClose={() => setConfirmar(false)}
          onConfirm={() => abrir.mutate()}
          loading={abrir.isPending}
          variant="info"
          title="¿Abrir la verificación de autenticidad?"
          message={`Se abre la verificación sobre ${recibidas} fichas${supuesto ? ` como ${supuesto}` : ''}. Desde ese momento el control del lote queda congelado.`}
          confirmText="Sí, abrir"
        />
      </div>
    );
  }

  if (lote.verificacion && lote.estado !== 'RESUELTO') {
    return (
      <div className="card">
        <div className="card-body flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">La verificación está en curso</h2>
            <p className="text-sm text-gray-500">
              {ETIQUETA_SUPUESTO_LOTE[lote.verificacion.supuesto] ?? lote.verificacion.supuesto}. Los próximos pasos se hacen en la pantalla de verificación.
            </p>
          </div>
          <Link to={`/verificacion-autenticidad?verificacion=${lote.verificacion.id}`} className="btn-primary gap-2">
            Ir a la verificación <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="alert-success flex items-center gap-2 text-sm">
      <CheckCircleIcon className="h-5 w-5 flex-shrink-0" /> La verificación de este lote terminó.
    </div>
  );
}

function Resumen({ lote }: { lote: LoteSepDetalle }) {
  const total = lote.solicitudes.length;
  const recibidas = lote.solicitudes.filter(s => s.fichaRecibida);
  const filas = [
    { etiqueta: 'Presentadas', valor: total },
    { etiqueta: 'Recibidas en papel', valor: recibidas.length },
    { etiqueta: 'No entregadas', valor: total - recibidas.length, aviso: total > recibidas.length },
    { etiqueta: 'Con observaciones', valor: recibidas.filter(s => s.conObservaciones).length },
    { etiqueta: 'Se verifican siempre', valor: recibidas.filter(s => desplazaAOtroPartido(s.resultadoControl)).length },
  ];
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="font-semibold text-gray-900">Resumen</h2>
      </div>
      <dl className="divide-y divide-gray-100">
        {filas.map(f => (
          <div key={f.etiqueta} className="flex items-center justify-between px-5 py-2.5 text-sm">
            <dt className="text-gray-600">{f.etiqueta}</dt>
            <dd className={`font-semibold ${f.aviso ? 'text-amber-700' : 'text-gray-900'}`}>{f.valor}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function EfectosSobreOtrosPartidos({ fichas }: { fichas: FichaSep[] }) {
  const efectos = fichas
    .filter(s => s.fichaRecibida)
    .flatMap(s => {
      if (desplazaAOtroPartido(s.resultadoControl)) {
        return [{ ficha: s, partido: partidosDondeFigura(s.detalleInterno), efecto: 'Baja en ese partido', clase: 'text-red-700' }];
      }
      const distrital = s.resultadoControl === 'DUPLICADO_DISTRITAL' || s.motivosControl.includes('DUPLICADO_DISTRITAL');
      if (distrital && s.constanciaNombre) {
        return [{ ficha: s, partido: partidosDondeFigura(s.detalleInterno), efecto: 'Bloqueada hasta revisar la constancia', clase: 'text-amber-700' }];
      }
      return [];
    });

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="font-semibold text-gray-900">Efectos sobre otros partidos al aprobar</h2>
        <p className="text-xs text-gray-500">Solo lo ve la SEP. El partido no conoce estos datos.</p>
      </div>
      {efectos.length === 0 ? (
        <p className="px-5 py-4 text-sm text-gray-500">Aprobar este lote no afecta a afiliados de otros partidos.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {efectos.map(({ ficha, partido, efecto, clase }) => (
            <li key={ficha.id} className="px-5 py-2.5 text-sm">
              <p className="font-medium text-gray-900">
                {ficha.numeroFicha && <span className="text-gray-400">#{ficha.numeroFicha} </span>}
                {ficha.apellido}, {ficha.nombre}
              </p>
              <p className="text-gray-500">Afiliado en {partido}</p>
              <p className={`text-xs font-medium ${clase}`}>{efecto}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BuscadorFichas({ valor, onCambio }: { valor: string; onCambio: (v: string) => void }) {
  return (
    <div className="relative w-full sm:w-64">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input value={valor} onChange={e => onCambio(e.target.value)} placeholder="Buscar DNI o nombre" className="input pl-9" />
    </div>
  );
}

function TablaFichas({ fichas }: { fichas: FichaSep[] }) {
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [busqueda, setBusqueda] = useState('');

  const ordenadas = useMemo(() => [...fichas].sort((a, b) =>
    (a.numeroFicha ?? Number.MAX_SAFE_INTEGER) - (b.numeroFicha ?? Number.MAX_SAFE_INTEGER)
    || a.apellido.localeCompare(b.apellido, 'es')), [fichas]);

  const filtros: { clave: Filtro; titulo: string; cumple: (s: FichaSep) => boolean }[] = [
    { clave: 'todas', titulo: 'Todas', cumple: () => true },
    { clave: 'observaciones', titulo: 'Con observaciones', cumple: s => s.conObservaciones },
    { clave: 'obligatorias', titulo: 'Se verifican siempre', cumple: s => s.fichaRecibida && desplazaAOtroPartido(s.resultadoControl) },
    { clave: 'no_entregadas', titulo: 'No entregadas', cumple: s => !s.fichaRecibida },
  ];
  const activo = filtros.find(f => f.clave === filtro)!;
  const texto = busqueda.trim().toLowerCase();
  const visibles = ordenadas
    .filter(activo.cumple)
    .filter(s => !texto || s.dni.includes(texto) || `${s.apellido} ${s.nombre}`.toLowerCase().includes(texto));

  return (
    <div className="card">
      <div className="card-header space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold text-gray-900">Fichas del lote</h2>
          <BuscadorFichas valor={busqueda} onCambio={setBusqueda} />
        </div>
        <div className="flex flex-wrap gap-2">
          {filtros.map(f => (
            <button
              key={f.clave}
              onClick={() => setFiltro(f.clave)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                f.clave === filtro ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.titulo} ({ordenadas.filter(f.cumple).length})
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2">N°</th>
              <th className="px-4 py-2">Afiliado</th>
              <th className="px-4 py-2">Fecha ficha</th>
              <th className="px-4 py-2">Control</th>
              <th className="px-4 py-2">Papel</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibles.map(s => (
              <tr key={s.id} className={s.fichaRecibida ? '' : 'bg-gray-50 text-gray-400'}>
                <td className="px-4 py-3 font-mono">{s.numeroFicha ?? '—'}</td>
                <td className="px-4 py-3">
                  <p className={`font-medium ${s.fichaRecibida ? 'text-gray-900' : ''}`}>{s.apellido}, {s.nombre}</p>
                  <p className="font-mono text-xs text-gray-500">DNI {s.dni}</p>
                </td>
                <td className="whitespace-nowrap px-4 py-3">{formatFechaCalendario(s.fechaAfiliacion)}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className={s.conObservaciones ? 'badge-warning' : 'badge-success'}>
                      {ETIQUETA_CONTROL[s.resultadoControl] ?? s.resultadoControl}
                    </span>
                    {s.fichaRecibida && desplazaAOtroPartido(s.resultadoControl) && (
                      <span className="badge-info">Se verifica siempre</span>
                    )}
                  </div>
                  {s.mensajesPartido.length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-xs text-gray-600">
                      {s.mensajesPartido.map(m => <li key={m}>• {m}</li>)}
                    </ul>
                  )}
                  {s.detalleInterno && (
                    <p className="mt-1 flex items-start gap-1 text-xs text-gray-500">
                      <LockClosedIcon className="mt-0.5 h-3 w-3 flex-shrink-0" /> {s.detalleInterno}
                    </p>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  {s.fichaRecibida
                    ? <span className="inline-flex items-center gap-1 text-green-700"><CheckCircleIcon className="h-4 w-4" /> Recibida</span>
                    : <span className="inline-flex items-center gap-1 text-amber-700"><ExclamationTriangleIcon className="h-4 w-4" /> No llegó</span>}
                </td>
              </tr>
            ))}
            {visibles.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No hay fichas para mostrar con este filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
